import React from 'react';
import {
  Box, Chip, IconButton, Stack, Tooltip, Typography,
} from '@mui/material';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import HearingDisabledIcon from '@mui/icons-material/HearingDisabled';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';

/**
 * Compact mic / conversation / listen controls for public demos.
 */
export default function DemoVoiceControls({
  hasStt = false,
  hasTts = false,
  isListening = false,
  isSpeaking = false,
  conversationMode = false,
  onToggleListen,
  onToggleConversation,
  onSpeak,
  onStopSpeak,
  disabled = false,
  dense = false,
  label = 'Voice',
  accent,
}) {
  if (!hasStt && !hasTts) return null;

  const size = dense ? 'small' : 'medium';
  const btnSx = {
    minWidth: dense ? 36 : 44,
    minHeight: dense ? 36 : 44,
    ...(accent ? { color: accent } : {}),
  };

  return (
    <Stack
      direction="row"
      spacing={0.5}
      alignItems="center"
      useFlexGap
      flexWrap="wrap"
      data-testid="demo-voice-controls"
    >
      {label ? (
        <Typography variant="caption" color="text.secondary" sx={{ mr: 0.25 }}>
          {label}
        </Typography>
      ) : null}

      {hasStt && (
        <Tooltip title={isListening ? 'Stop listening' : 'Dictate / talk'}>
          <span>
            <IconButton
              size={size}
              onClick={onToggleListen}
              disabled={disabled}
              aria-label={isListening ? 'Stop listening' : 'Start voice input'}
              sx={{
                ...btnSx,
                color: isListening ? 'error.main' : (accent || 'text.secondary'),
                bgcolor: isListening ? 'action.hover' : 'transparent',
              }}
            >
              {isListening ? <MicOffIcon fontSize={size} /> : <MicIcon fontSize={size} />}
            </IconButton>
          </span>
        </Tooltip>
      )}

      {hasStt && hasTts && onToggleConversation && (
        <Tooltip title={conversationMode
          ? 'Conversation on — speak, then hear the reply'
          : 'Start audio conversation (speak ↔ listen)'}
        >
          <span>
            <IconButton
              size={size}
              onClick={onToggleConversation}
              disabled={disabled}
              aria-label={conversationMode ? 'End audio conversation' : 'Start audio conversation'}
              aria-pressed={conversationMode}
              sx={{
                ...btnSx,
                color: conversationMode ? '#fff' : (accent || 'text.secondary'),
                bgcolor: conversationMode ? (accent || 'primary.main') : 'transparent',
                '&:hover': conversationMode
                  ? { bgcolor: accent || 'primary.dark' }
                  : undefined,
              }}
            >
              <RecordVoiceOverIcon fontSize={size} />
            </IconButton>
          </span>
        </Tooltip>
      )}

      {hasTts && (onSpeak || onStopSpeak) && (
        <Tooltip title={isSpeaking ? 'Stop speaking' : 'Listen to reply'}>
          <span>
            <IconButton
              size={size}
              onClick={isSpeaking ? onStopSpeak : onSpeak}
              disabled={disabled || (!isSpeaking && !onSpeak)}
              aria-label={isSpeaking ? 'Stop speaking' : 'Listen to reply'}
              sx={btnSx}
            >
              {isSpeaking ? <HearingDisabledIcon fontSize={size} /> : <VolumeUpIcon fontSize={size} />}
            </IconButton>
          </span>
        </Tooltip>
      )}

      {(isListening || conversationMode || isSpeaking) && (
        <Chip
          size="small"
          label={isSpeaking ? 'Speaking…' : (isListening ? 'Listening…' : 'Conversation')}
          color={isSpeaking ? 'default' : 'error'}
          variant={conversationMode && !isListening && !isSpeaking ? 'outlined' : 'filled'}
          sx={{ height: 22, fontSize: '0.7rem' }}
        />
      )}
    </Stack>
  );
}

/** InputAdornment-friendly mic toggle for TextFields. */
export function DemoVoiceMicAdornment({
  hasSupport,
  isListening,
  onToggle,
  disabled,
}) {
  if (!hasSupport) return null;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', mr: 0.5 }}>
      <Tooltip title={isListening ? 'Stop dictation' : 'Dictate with mic'}>
        <span>
          <IconButton
            size="small"
            onClick={onToggle}
            disabled={disabled}
            edge="end"
            aria-label={isListening ? 'Stop dictation' : 'Dictate with mic'}
            sx={{
              color: isListening ? 'error.main' : 'text.secondary',
              bgcolor: isListening ? 'action.hover' : 'transparent',
            }}
          >
            {isListening ? <MicOffIcon fontSize="small" /> : <MicIcon fontSize="small" />}
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );
}
